import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as microsoftTeams from '@microsoft/teams-js';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { FileList } from './components/FileList';
import { TabConfig } from './components/TabConfig';
import { applyTeamsLocale } from './i18n';
import './styles/App.css';

// ---------------------------------------------------------------------------
// eruda – in-app DevTools console
// Toggle with: Cmd+L (keyboard) or 3-finger tap (touch)
// ---------------------------------------------------------------------------
function useEruda() {
  const erudaRef = useRef<any>(null);
  const touchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggle = useCallback(() => {
    const eruda = erudaRef.current;
    if (!eruda) return;
    // _isShow is the internal flag; the public helpers are show()/hide()
    const visible = (eruda as any)._devTools?._isShow ?? false;
    if (visible) {
      eruda.hide();
    } else {
      eruda.show();
    }
  }, []);

  useEffect(() => {
    let destroyed = false;

    // Dynamically import so the chunk is separate and tree-shakeable
    import('eruda').then((mod) => {
      if (destroyed) return;
      const eruda = mod.default ?? mod;
      eruda.init();
      eruda.hide(); // start hidden
      erudaRef.current = eruda;
    });

    // --- Keyboard shortcut: Cmd+L / Ctrl+L ---
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    // --- 3-finger tap detection ---
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 3) {
        // Start a short timer so we only fire on a *tap*, not a swipe
        touchTimerRef.current = setTimeout(() => {
          toggle();
        }, 300);
      }
    };
    const onTouchEnd = (_e: TouchEvent) => {
      // If the user lifts fingers quickly that's fine – the timer is still running.
      // But if they move / swipe we cancel in onTouchMove.
    };
    const onTouchMove = (_e: TouchEvent) => {
      // Cancel if fingers move (swipe gesture, not a tap)
      if (touchTimerRef.current) {
        clearTimeout(touchTimerRef.current);
        touchTimerRef.current = null;
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      destroyed = true;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchmove', onTouchMove);
      if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
      if (erudaRef.current) {
        erudaRef.current.destroy();
        erudaRef.current = null;
      }
    };
  }, [toggle]);
}

const App: React.FC = () => {
  const { t } = useTranslation();
  const [isConfigMode, setIsConfigMode] = useState<boolean | null>(null);

  // eruda – in-app DevTools console (hidden by default, toggle with Cmd+L or 3-finger tap)
  // Uncomment the next line to enable:
  // useEruda();

  useEffect(() => {
    const checkMode = async () => {
      try {
        // Initialize Teams SDK
        await microsoftTeams.app.initialize();
        
        // Get the current page context to determine if we're in config mode
        const context = await microsoftTeams.app.getContext();

        // Apply language from Teams context (e.g. "de-de", "en-us").
        // In the Desktop app navigator.language may not reflect the user's
        // Teams language setting, so we must read it from the SDK context.
        applyTeamsLocale(context.app?.locale);
        
        // Check if we're in the configuration page
        // This is indicated by the page.frameContext being 'settings'
        const isConfig = context.page?.frameContext === 'settings';
        setIsConfigMode(isConfig);
        
        console.log('Teams context:', context);
        console.log('Frame context:', context.page?.frameContext);
        console.log('Teams locale:', context.app?.locale);
        console.log('Is config mode:', isConfig);
      } catch (error) {
        console.error('Failed to initialize Teams:', error);
        // If Teams SDK fails, assume we're not in config mode
        setIsConfigMode(false);
      }
    };

    checkMode();
  }, []);

  // Show loading state while determining mode
  if (isConfigMode === null) {
    return (
      <FluentProvider theme={webLightTheme}>
        <div className="app" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <div>{t('loading')}</div>
        </div>
      </FluentProvider>
    );
  }

  // Show configuration UI if in config mode
  if (isConfigMode) {
    return <TabConfig />;
  }

  // Show normal file list UI
  return (
    <FluentProvider theme={webLightTheme}>
      <div className="app">
        <FileList />
      </div>
    </FluentProvider>
  );
};

export default App;
